import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

interface StorageImageProps {
  storageKey: string;
  token: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}

const urlCache = new Map<string, string>();

export default function StorageImage({ storageKey, token, alt = '', className, style }: StorageImageProps) {
  const [url, setUrl] = useState<string | null>(urlCache.get(storageKey) ?? null);
  const [loading, setLoading] = useState(!urlCache.has(storageKey));
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (urlCache.has(storageKey)) {
      setUrl(urlCache.get(storageKey)!);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    api.getDownloadUrl(storageKey, token)
      .then((result) => {
        if (!cancelled && mountedRef.current) {
          urlCache.set(storageKey, result.downloadUrl);
          setUrl(result.downloadUrl);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) {
          setError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [storageKey, token]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          color: '#9ca3af',
          fontSize: '0.75rem',
          minWidth: 60,
          minHeight: 60,
          borderRadius: '6px',
          ...style,
        }}
      >
        Loading...
      </div>
    );
  }

  if (error || !url) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fef2f2',
          color: '#ef4444',
          fontSize: '0.75rem',
          minWidth: 60,
          minHeight: 60,
          borderRadius: '6px',
          ...style,
        }}
      >
        Failed to load
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className={className}
      style={{ borderRadius: '6px', objectFit: 'cover', ...style }}
      onError={() => { setError(true); setUrl(null); }}
    />
  );
}
